import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';

import { API } from '@/shared/api';
import { getIsUserLeftProjectPage } from '@/shared/lib/location';
import { TUpdatingSegmentData } from '@/shared/types/Editor';
import { Project } from '@/shared/types/Project';

import { RootState } from '../../index';
import { removeSegmentDataToSave, setIsVoiceSuggesting, updSegment, updSegmentFromServer } from '../EditorSlice';

type UpdateSegments = {
  changedData: TUpdatingSegmentData[];
  project?: Project;
};

export const updateSegments = createAsyncThunk<void, UpdateSegments, { state: RootState }>(
  'editor/updateSegment',
  async ({ changedData, project }: UpdateSegments, { dispatch, getState }) => {
    try {
      if (!project) {
        return;
      }

      const { segments: updatedSegments } = await API.updateSegments(project.id, changedData);

      if (!updatedSegments.length) {
        throw new Error();
      }

      const isUserLeftProject = getIsUserLeftProjectPage(project.id);

      if (isUserLeftProject) {
        return;
      }

      updatedSegments.forEach((segment) => {
        dispatch(updSegmentFromServer(segment));
      });
    } catch {
      enqueueSnackbar("We couldn't save the segment. Please try again", {
        autoHideDuration: 6000,
      });

      // Add isSavingError to the segment to highlight it
      changedData.forEach((segment) => {
        dispatch(updSegment({ id: segment.id, isSavingError: true }));
        // Removing from the store the data that the user managed to update while the segment was being saved
        dispatch(removeSegmentDataToSave({ segmentId: segment.id }));
      });

      const {
        editor: { segmentsFromServerMap },
      } = getState();

      changedData.forEach((segment) => {
        const segmentFromServer = segmentsFromServerMap[segment.id];

        // Updating the data with the actual information from segmentsFromServerMap
        if (segmentFromServer) {
          dispatch(updSegment(segmentFromServer));
        }

        if ('speaker' in changedData) {
          dispatch(setIsVoiceSuggesting(false));
        }
      });
    }
  },
);
